import { useEffect, useState } from 'react';
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

const DETECTION_INTERVAL_MS = 120;
const STABLE_FACE_MS = 900;
const MIN_CONSECUTIVE_DETECTIONS = 2;

export type FaceDetectorState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  stableFaceDetected: boolean;
  errorMessage: string | null;
};

export class FaceStabilityTracker {
  private detectedSince: number | null = null;
  private consecutiveDetections = 0;

  update(detected: boolean, now: number): boolean {
    if (!detected) {
      this.detectedSince = null;
      this.consecutiveDetections = 0;
      return false;
    }
    this.detectedSince ??= now;
    this.consecutiveDetections += 1;
    return (
      this.consecutiveDetections >= MIN_CONSECUTIVE_DETECTIONS
      && now - this.detectedSince >= STABLE_FACE_MS
    );
  }
}

const errorText = (error: unknown) => (
  error instanceof Error && error.message ? error.message : String(error)
);

export function useFaceDetector(
  videoElement: HTMLVideoElement | null,
  enabled: boolean,
): FaceDetectorState {
  const [state, setState] = useState<FaceDetectorState>({
    status: 'idle',
    stableFaceDetected: false,
    errorMessage: null,
  });

  useEffect(() => {
    if (!enabled || !videoElement) {
      setState({ status: 'idle', stableFaceDetected: false, errorMessage: null });
      return;
    }

    let active = true;
    let detector: FaceDetector | null = null;
    let timer: number | null = null;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const stability = new FaceStabilityTracker();
    setState({ status: 'loading', stableFaceDetected: false, errorMessage: null });

    void (async () => {
      try {
        if (!context) throw new Error('顔検出用のCanvasを作成できません');
        const fileset = await FilesetResolver.forVisionTasks('/models/mediapipe/wasm');
        if (!active) return;
        detector = await FaceDetector.createFromOptions(fileset, {
          baseOptions: {
            modelAssetPath: '/models/mediapipe/blaze_face_short_range.tflite',
          },
          runningMode: 'VIDEO',
          minDetectionConfidence: 0.5,
          minSuppressionThreshold: 0.3,
        });
        if (!active) {
          detector.close();
          detector = null;
          return;
        }
        setState({ status: 'ready', stableFaceDetected: false, errorMessage: null });

        const detect = () => {
          if (!active || !detector) return;
          if (
            videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
            || videoElement.videoWidth === 0
            || videoElement.videoHeight === 0
          ) {
            timer = window.setTimeout(detect, DETECTION_INTERVAL_MS);
            return;
          }
          try {
            canvas.width = videoElement.videoHeight;
            canvas.height = videoElement.videoWidth;
            context.save();
            context.translate(canvas.width, 0);
            context.rotate(Math.PI / 2);
            context.drawImage(videoElement, 0, 0, videoElement.videoWidth, videoElement.videoHeight);
            context.restore();
            const now = performance.now();
            const result = detector.detectForVideo(canvas, now);
            if (stability.update(result.detections.length > 0, now)) {
              setState({
                status: 'ready',
                stableFaceDetected: true,
                errorMessage: null,
              });
              return;
            }
          } catch (error) {
            setState({
              status: 'error',
              stableFaceDetected: false,
              errorMessage: errorText(error),
            });
            return;
          }
          timer = window.setTimeout(detect, DETECTION_INTERVAL_MS);
        };
        detect();
      } catch (error) {
        if (!active) return;
        setState({
          status: 'error',
          stableFaceDetected: false,
          errorMessage: errorText(error),
        });
      }
    })();

    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
      detector?.close();
    };
  }, [enabled, videoElement]);

  return state;
}
